// Canonical country list for profile forms.
//
// DELIBERATELY NOT /api/players/countries. That endpoint returns the DISTINCT
// values found on existing `players.nationality` rows (with player counts) —
// it is a *discovery facet*, derived from whoever happens to be in the DB. A
// coach from a country with no players in the corpus could not select their
// own country from it, and it also carries junk the bulk import produced
// (team names like "Real Madrid" landed in that column). This list is a fixed
// reference set instead, with no dependency on player data.
//
// SPELLING IS LOAD-BEARING. `src/lib/regions.ts` maps country name → region
// slug for /browse, matching on these exact strings (case-insensitively).
// Where a common ISO name differs from what regions.ts already uses, the
// regions.ts spelling wins so region bucketing keeps working:
//   USA          (not "United States")
//   South Korea  (not "Korea, Republic of")
//   Ivory Coast  (not "Côte d'Ivoire")
//   Czech Republic, Cape Verde, United Kingdom
// If you add a country here that should bucket into a region, add it to
// regions.ts too — unmapped names fall through to the "Other" region rather
// than erroring, so a mismatch degrades quietly and is easy to miss.
//
// Free-text values stored before this list existed (`Vietnam`, `Canada`) are
// both present, so no data migration is required. Anything stored that is NOT
// in this list must still render — see countryOptions() below.

export const COUNTRIES: readonly string[] = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada',
  'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China',
  'Colombia', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus',
  'Czech Republic', 'Democratic Republic of the Congo', 'Denmark',
  'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Estonia',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Georgia', 'Germany',
  'Ghana', 'Greece', 'Guam', 'Guatemala', 'Guinea', 'Haiti', 'Honduras',
  'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq',
  'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
  'Lebanon', 'Liberia', 'Libya', 'Lithuania', 'Luxembourg', 'Macau',
  'Madagascar', 'Malawi', 'Malaysia', 'Mali', 'Malta', 'Mauritania',
  'Mexico', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia', 'Norway', 'Oman',
  'Pakistan', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Puerto Rico', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Serbia', 'Sierra Leone',
  'Singapore', 'Slovakia', 'Slovenia', 'Somalia', 'South Africa',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand',
  'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
  'Turkmenistan', 'USA', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States Virgin Islands', 'Uruguay', 'Uzbekistan',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
]

/**
 * Options for a country <select>, given whatever is currently stored.
 *
 * If `current` is a non-empty value that is not in COUNTRIES, it is prepended
 * so the select can render it. Losing a coach's saved country to a dropdown
 * refactor is worse than showing one odd entry — the stored column is free
 * text and predates this list.
 */
export function countryOptions(current: string | null | undefined): readonly string[] {
  if (!current) return COUNTRIES
  const match = COUNTRIES.some((c) => c.toLowerCase() === current.toLowerCase())
  return match ? COUNTRIES : [current, ...COUNTRIES]
}
